import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { zodResolver } from "@hookform/resolvers/zod";
import { showCombinedErrorToast } from "~/components/shared/error-toast";
import CustomFormWrapper from "~/components/shared/form-wrapper";
import { Button, Stepper } from "~/components/ui";

import { CreateCOSHHSchema, TCreateCOSHH } from "./types";
import HeaderStep from "./steps/header";
import ChemicalsStep from "./steps/chemicals";
import SummaryStep from "./steps/summary";
import { useCreateCOSHHAssessmentMutation } from "~/redux/api/dailyOps";

const COSHHForm = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [createCOSHH] = useCreateCOSHHAssessmentMutation();

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<TCreateCOSHH>({
    resolver: zodResolver(CreateCOSHHSchema),
    mode: "onSubmit",
    defaultValues: {
      reference: `COSHH-${new Date().getTime()}`,
      title: "",
      description: "",
      assessmentDate: new Date(),
      chemicals: [],
    },
  });

  const onSubmit = async (data: TCreateCOSHH) => {
    try {
      const payload = {
        ...data,
        siteId: data.siteId?.value,
        evaluatorId: data.evaluatorId?.value,
        chemicals: data.chemicals.map((c) => ({
          ...c,
          chemicalRegisterEntryId: c.chemicalRegisterEntryId.value,
          initialRiskLevelId: c.initialRiskLevelId?.value,
          residualRiskLevelId: c.residualRiskLevelId?.value,
        })),
      };

      await createCOSHH({ body: payload }).unwrap();
      toast.success("COSHH Assessment submitted successfully");
      navigate("/daily-ops/coshh");
    } catch (err) {
      toast.error("Failed to submit COSHH Assessment");
    }
  };

  const STEPS = useMemo(
    () => [
      {
        title: "Details",
        description: "General details",
        content: (
          <HeaderStep control={control} errors={errors} register={register} />
        ),
      },
      {
        title: "Chemicals",
        description: "Identify chemicals and usage",
        content: <ChemicalsStep control={control} errors={errors} />,
      },
      {
        title: "Summary",
        description: "Review and submit",
        content: <SummaryStep watch={watch} />,
      },
    ],
    [control, errors, register, watch],
  );

  const goToNextStep = useCallback(() => {
    setCurrentStep((prev) => (prev === STEPS.length - 1 ? prev : prev + 1));
  }, [STEPS.length]);

  const goToPreviousStep = useCallback(() => {
    setCurrentStep((prev) => (prev <= 0 ? prev : prev - 1));
  }, []);

  const [isSubmiting, setIsSubmiting] = useState(false);

  useEffect(() => {
    if (isSubmiting && Object.values(errors).length > 0) {
      showCombinedErrorToast(errors);
      setIsSubmiting(false);
    }
    return () => {
      setIsSubmiting(false);
    };
  }, [isSubmiting, errors]);

  return (
    <div className="relative w-full">
      <form id="create-coshh" onSubmit={handleSubmit(onSubmit)}>
        <CustomFormWrapper className="">
          <div className="mx-auto w-full rounded-xl border border-neutral-200 bg-white px-10 py-8 mb-6">
            <Stepper
              currentStep={currentStep}
              steps={STEPS}
              setCurrentStep={setCurrentStep}
            />
          </div>

          <div className="w-full">{STEPS[currentStep].content}</div>

          <div className="flex items-center justify-between pt-8 pb-10">
            <Button
              onClick={goToPreviousStep}
              type="button"
              variant="outline"
              disabled={currentStep === 0}
            >
              Back
            </Button>
            {currentStep !== STEPS.length - 1 ? (
              <Button onClick={goToNextStep} type="button">
                Next
              </Button>
            ) : (
              <Button type="submit" onClick={() => setIsSubmiting(true)}>
                Submit COSHH
              </Button>
            )}
          </div>
        </CustomFormWrapper>
      </form>
    </div>
  );
};

export default COSHHForm;
